/**
 * Animates the product-family-timeline section when it scrolls into view:
 *   1. The horizontal center line grows from left to right
 *   2. Items appear one by one (opacity + translateY)
 *   3. The currently appearing item has a filled shield icon;
 *      all others remain outlined
 *   4. Final state: all items visible, all shields outlined
 *
 * Fires only on desktop (>= 750px). Mobile shows everything immediately.
 * Fires only once per page load (IntersectionObserver unobserves after trigger).
 * Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  const LINE_START_DELAY = 150;
  const LINE_DURATION = 1000;
  const ITEM_STAGGER = 400;
  const FIRST_ITEM_DELAY = 350;
  const MOBILE_BREAKPOINT = 750;

  function initAnimation(section) {
    const timeline = section.querySelector('.product-family-timeline__timeline');
    if (!timeline) return;

    const items = Array.from(timeline.querySelectorAll('.product-family-timeline__item'));
    if (items.length === 0) return;

    const nodes = items.map((item) => item.querySelector('.product-family-timeline__node'));

    // mobile: show everything immediately, no animation
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      section.removeAttribute('data-product-family-timeline-animate');
      return;
    }

    // reduced motion: show everything immediately
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      section.classList.add('product-family-timeline--line-visible');
      items.forEach((item) => item.classList.add('product-family-timeline__item--visible'));
      return;
    }

    let hasAnimated = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            hasAnimated = true;
            observer.unobserve(section);
            runAnimation(section, items, nodes);
          }
        });
      },
      {
        // use threshold 0 so sections already in viewport on load fire immediately
        rootMargin: '0px 0px -50px 0px',
        threshold: 0,
      }
    );

    observer.observe(section);
  }

  function runAnimation(section, items, nodes) {
    // step 1: grow the horizontal line
    setTimeout(() => {
      section.classList.add('product-family-timeline--line-visible');
    }, LINE_START_DELAY);

    // step 2: reveal items one by one
    items.forEach((item, index) => {
      const delay = LINE_START_DELAY + FIRST_ITEM_DELAY + index * ITEM_STAGGER;

      setTimeout(() => {
        // deactivate previous node (back to outlined)
        if (index > 0 && nodes[index - 1]) {
          nodes[index - 1].classList.remove('product-family-timeline__node--active');
        }

        // activate current node (filled shield)
        if (nodes[index]) {
          nodes[index].classList.add('product-family-timeline__node--active');
        }

        item.classList.add('product-family-timeline__item--visible');

        // last item: remove active after transition finishes → back to outlined
        if (index === items.length - 1) {
          setTimeout(() => {
            if (nodes[index]) {
              nodes[index].classList.remove('product-family-timeline__node--active');
            }
          }, 600);
        }
      }, delay);
    });
  }

  function init() {
    const sections = document.querySelectorAll('[data-product-family-timeline-animate]');
    sections.forEach(initAnimation);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor support
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('[data-product-family-timeline-animate]');
      if (section) {
        section.classList.add('product-family-timeline--line-visible');
        section.querySelectorAll('.product-family-timeline__item').forEach((item) => {
          item.classList.add('product-family-timeline__item--visible');
        });
      }
    });
  }
})();
